import { useApproveFollowRequestMutation } from "Api/Mutations/Social/useApproveFollowRequestMutation";
import { useDeleteFollowRequestMutation } from "Api/Mutations/Social/useDeleteFollowRequestMutation";
import { useFollowUserMutation } from "Api/Mutations/Social/useFollowUserMutation";
import { useUnblockUserMutation } from "Api/Mutations/Social/useUnblockUserMutation";
import { useUnfollowUserMutation } from "Api/Mutations/Social/useUnfollowUserMutation";
import { useMutualFollowConfirm } from "Components/Social/useMutualFollowConfirm";
import type {
  PublicProfileDto,
  SocialRelationshipDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Social.PublicProfile;

/**
 * The graph mutations of one public profile, addressed to that persona and with the mutual-follow
 * confirm applied where the action completes a pair:
 *
 * - following back is instant only for a public target — a private one gets a request, and the
 *   pair becomes mutual only when THEY approve, so their side confirms then;
 * - approving someone the viewer already follows completes the pair.
 *
 * `isMutating` is true while any of them runs, so the buttons disable together; the host renders
 * `MutualFollowConfirmDialog` from `mutualConfirmDialogProps`.
 *
 * The actions are plain closures, recreated each render on purpose: they end up as button
 * onClick handlers, nothing memoizes on them, so useCallback would buy nothing.
 */
export const useProfileGraphActions = (
  profile: PublicProfileDto,
  relationship: SocialRelationshipDto | undefined,
) => {
  const { t } = useResource();
  const { confirmMutual, mutualConfirmDialogProps } = useMutualFollowConfirm();

  const { mutate: follow, isPending: isFollowPending } =
    useFollowUserMutation();
  const { mutate: unfollow, isPending: isUnfollowPending } =
    useUnfollowUserMutation();
  const { mutate: approve, isPending: isApprovePending } =
    useApproveFollowRequestMutation();
  const { mutate: deleteRequest, isPending: isDeletePending } =
    useDeleteFollowRequestMutation();
  const { mutate: unblock, isPending: isUnblockPending } =
    useUnblockUserMutation();

  const id = profile.identityUserPublicID;
  const candidate = {
    handle: profile.handle,
    isFullMoone: profile.isFullMoone,
  };

  const isFollowBackCompletingPair =
    relationship?.isFollowedBy && !relationship.requiresFollowApproval;

  return {
    follow: () =>
      isFollowBackCompletingPair
        ? confirmMutual(candidate, t(RESOURCES.FollowBack), () => follow(id))
        : follow(id),
    unfollow: () => unfollow(id),
    approve: () =>
      relationship?.isFollowed
        ? confirmMutual(candidate, t(RESOURCES.ApproveRequest), () =>
            approve(id),
          )
        : approve(id),
    deleteRequest: () => deleteRequest(id),
    unblock: () => unblock(id),
    isMutating:
      isFollowPending ||
      isUnfollowPending ||
      isApprovePending ||
      isDeletePending ||
      isUnblockPending,
    mutualConfirmDialogProps,
  };
};
